/**
 * Blushi - Boost Commerce TURBO Integration
 * Custom widget integration and brand styling
 */

(function() {
  'use strict';

  // Blushi Boost Commerce Configuration
  const BlushiBoost = {
    config: {
      currency: Shopify?.currency?.active || 'USD',
      moneyFormat: Shopify?.currency?.format || '${{amount}}',
      animations: true,
      quickViewEnabled: true,
      wishlistEnabled: true,
      recentlyViewedLimit: 8,
      recommendationsLimit: 4,
    },

    // Initialize all customizations
    init() {
      if (typeof boostWidgetIntegration === 'undefined') {
        console.log('[Blushi] Boost Commerce not detected, waiting...');
        document.addEventListener('boost:ready', () => this.setup());
        return;
      }
      this.setup();
    },

    setup() {
      console.log('[Blushi] Initializing Boost Commerce customizations');
      
      this.registerProductCardCustomization();
      this.registerSearchCustomization();
      this.registerFilterCustomization();
      this.registerPaginationCustomization();
      this.registerToolbarCustomization();
      this.bindEvents();
    },

    // Custom Product Card Template
    registerProductCardCustomization() {
      if (typeof boostWidgetIntegration === 'undefined') return;

      boostWidgetIntegration.regisCustomization('product-item', {
        template: function(product) {
          const hasComparePrice = product.compare_at_price_max > product.price_min;
          const isSoldOut = !product.available;
          const isOnSale = hasComparePrice;
          const isNew = BlushiBoost.isNewProduct(product);
          const isBestseller = product.tags && product.tags.includes('bestseller');
          
          const primaryImage = product.images_info?.[0]?.src || product.featured_image;
          const secondaryImage = product.images_info?.[1]?.src || null;
          
          let labelsHtml = '';
          if (isSoldOut) {
            labelsHtml += '<span class="boost-pfs-filter-product-item-label-soldout">Sold Out</span>';
          } else {
            if (isBestseller) {
              labelsHtml += '<span class="boost-pfs-filter-product-item-label-bestseller">Bestseller</span>';
            }
            if (isOnSale) {
              const discount = Math.round((1 - product.price_min / product.compare_at_price_max) * 100);
              labelsHtml += `<span class="boost-pfs-filter-product-item-label-sale">${discount}% Off</span>`;
            }
            if (isNew) {
              labelsHtml += '<span class="boost-pfs-filter-product-item-label-new">New</span>';
            }
          }

          return `
            <div class="boost-pfs-filter-product-item" data-product-id="${product.id}" data-handle="${product.handle}">
              <div class="boost-pfs-filter-product-item-image-wrapper">
                <a href="/products/${product.handle}">
                  <img 
                    class="boost-pfs-filter-product-item-image" 
                    src="${primaryImage}" 
                    alt="${BlushiBoost.escapeHtml(product.title)}"
                    loading="lazy"
                    ${secondaryImage ? `data-hover-src="${secondaryImage}"` : ''}
                  />
                </a>
                ${labelsHtml ? `<div class="boost-pfs-filter-product-item-label">${labelsHtml}</div>` : ''}
                ${!isSoldOut ? `
                  <button 
                    class="boost-pfs-filter-product-item-quick-add" 
                    data-product-handle="${product.handle}"
                    data-variant-id="${product.variants?.[0]?.id || ''}"
                    aria-label="Quick add ${BlushiBoost.escapeHtml(product.title)}"
                  >
                    Quick Add
                  </button>
                ` : ''}
                <button 
                  class="boost-pfs-filter-product-item-wishlist" 
                  data-product-id="${product.id}"
                  aria-label="Add to wishlist"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
                  </svg>
                </button>
              </div>
              <div class="boost-pfs-filter-product-item-inner">
                ${product.vendor ? `<div class="boost-pfs-filter-product-item-vendor">${BlushiBoost.escapeHtml(product.vendor)}</div>` : ''}
                <h3 class="boost-pfs-filter-product-item-title">
                  <a href="/products/${product.handle}">${BlushiBoost.escapeHtml(product.title)}</a>
                </h3>
                <div class="boost-pfs-filter-product-item-price">
                  ${isOnSale ? `
                    <span class="boost-pfs-filter-product-item-price-sale">${BlushiBoost.formatMoney(product.price_min)}</span>
                    <span class="boost-pfs-filter-product-item-price-compare">${BlushiBoost.formatMoney(product.compare_at_price_max)}</span>
                  ` : `
                    <span class="boost-pfs-filter-product-item-price-regular">${BlushiBoost.formatMoney(product.price_min)}</span>
                  `}
                </div>
              </div>
            </div>
          `;
        }
      });
    },

    // Custom Search Suggestion Template
    registerSearchCustomization() {
      if (typeof boostWidgetIntegration === 'undefined') return;

      boostWidgetIntegration.regisCustomization('search-suggestion', {
        product: {
          template: function(product) {
            const image = product.images_info?.[0]?.src || product.featured_image || '';
            return `
              <a href="/products/${product.handle}" class="boost-pfs-search-suggestion-item">
                <img src="${image}" alt="${BlushiBoost.escapeHtml(product.title)}" loading="lazy" />
                <div class="boost-pfs-search-suggestion-item-content">
                  <div class="boost-pfs-search-suggestion-item-title">${BlushiBoost.escapeHtml(product.title)}</div>
                  <div class="boost-pfs-search-suggestion-item-price">${BlushiBoost.formatMoney(product.price_min)}</div>
                </div>
              </a>
            `;
          }
        },
        collection: {
          template: function(collection) {
            return `
              <a href="/collections/${collection.handle}" class="boost-pfs-search-suggestion-collection">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="7" height="7" rx="1"/>
                  <rect x="14" y="3" width="7" height="7" rx="1"/>
                  <rect x="3" y="14" width="7" height="7" rx="1"/>
                  <rect x="14" y="14" width="7" height="7" rx="1"/>
                </svg>
                ${BlushiBoost.escapeHtml(collection.title)}
              </a>
            `;
          }
        }
      });
    },

    // Custom Filter Option Template
    registerFilterCustomization() {
      if (typeof boostWidgetIntegration === 'undefined') return;

      boostWidgetIntegration.regisCustomization('filter-option', {
        // Custom swatch display for colors
        swatch: {
          template: function(item) {
            const colorValue = item.value.toLowerCase().replace(/\s/g, '-');
            const isImage = item.image && item.image.length > 0;
            
            return `
              <div 
                class="boost-pfs-filter-option-swatch-item ${item.selected ? 'selected' : ''}" 
                data-value="${BlushiBoost.escapeHtml(item.value)}"
                title="${BlushiBoost.escapeHtml(item.value)}"
                role="checkbox"
                aria-checked="${item.selected}"
                tabindex="0"
              >
                ${isImage ? 
                  `<img src="${item.image}" alt="${BlushiBoost.escapeHtml(item.value)}" />` :
                  `<span class="boost-pfs-filter-option-swatch-color" style="background-color: ${BlushiBoost.getColorHex(colorValue)};"></span>`
                }
              </div>
            `;
          }
        }
      });
    },

    // Custom Pagination
    registerPaginationCustomization() {
      if (typeof boostWidgetIntegration === 'undefined') return;

      boostWidgetIntegration.regisCustomization('pagination', {
        template: function(data) {
          if (data.totalPages <= 1) return '';
          
          let html = '<nav class="boost-pfs-pagination" aria-label="Products pagination">';
          
          // Previous
          if (data.currentPage > 1) {
            html += `<a href="#" class="boost-pfs-pagination-prev" data-page="${data.currentPage - 1}" aria-label="Previous page">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="m15 18-6-6 6-6"/>
              </svg>
            </a>`;
          }
          
          // Page numbers
          const range = BlushiBoost.getPaginationRange(data.currentPage, data.totalPages);
          range.forEach(page => {
            if (page === '...') {
              html += '<span class="boost-pfs-pagination-dots">...</span>';
            } else {
              html += `<a href="#" class="boost-pfs-pagination-page ${page === data.currentPage ? 'active' : ''}" data-page="${page}">${page}</a>`;
            }
          });
          
          // Next
          if (data.currentPage < data.totalPages) {
            html += `<a href="#" class="boost-pfs-pagination-next" data-page="${data.currentPage + 1}" aria-label="Next page">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </a>`;
          }
          
          html += '</nav>';
          return html;
        }
      });
    },

    // Custom Toolbar
    registerToolbarCustomization() {
      if (typeof boostWidgetIntegration === 'undefined') return;

      boostWidgetIntegration.regisCustomization('toolbar', {
        viewMode: {
          grid: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="7" height="7" rx="1"/>
            <rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/>
            <rect x="14" y="14" width="7" height="7" rx="1"/>
          </svg>`,
          list: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>`
        }
      });
    },

    // Event Bindings
    bindEvents() {
      // Quick Add to Cart
      document.addEventListener('click', (e) => {
        const quickAddBtn = e.target.closest('.boost-pfs-filter-product-item-quick-add');
        if (quickAddBtn) {
          e.preventDefault();
          this.handleQuickAdd(quickAddBtn);
        }
      });

      // Wishlist Toggle
      document.addEventListener('click', (e) => {
        const wishlistBtn = e.target.closest('.boost-pfs-filter-product-item-wishlist');
        if (wishlistBtn) {
          e.preventDefault();
          this.handleWishlistToggle(wishlistBtn);
        }
      });

      // Image Hover Effect
      document.addEventListener('mouseenter', (e) => {
        if (!e.target || typeof e.target.closest !== 'function') return;
        const productCard = e.target.closest('.boost-pfs-filter-product-item');
        if (productCard) {
          const img = productCard.querySelector('.boost-pfs-filter-product-item-image');
          const hoverSrc = img?.dataset.hoverSrc;
          if (hoverSrc && !img.dataset.originalSrc) {
            img.dataset.originalSrc = img.src;
            img.src = hoverSrc;
          }
        }
      }, true);

      document.addEventListener('mouseleave', (e) => {
        if (!e.target || typeof e.target.closest !== 'function') return;
        const productCard = e.target.closest('.boost-pfs-filter-product-item');
        if (productCard) {
          const img = productCard.querySelector('.boost-pfs-filter-product-item-image');
          if (img?.dataset.originalSrc) {
            img.src = img.dataset.originalSrc;
          }
        }
      }, true);

      // Filter animations
      document.addEventListener('boost:filter:applied', () => {
        if (this.config.animations) {
          this.animateProducts();
        }
      });
    },

    // Quick Add to Cart Handler
    async handleQuickAdd(button) {
      const variantId = button.dataset.variantId;
      const productHandle = button.dataset.productHandle;
      
      if (!variantId) {
        // Open product page if no variant
        window.location.href = `/products/${productHandle}`;
        return;
      }

      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Adding...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });

        if (response.ok) {
          button.textContent = 'Added!';
          button.classList.add('success');
          
          // Trigger cart update event
          document.dispatchEvent(new CustomEvent('cart:updated'));
          
          // Open cart drawer if available
          const cartDrawer = document.querySelector('[data-cart-drawer]');
          if (cartDrawer) {
            cartDrawer.classList.add('open');
          }

          setTimeout(() => {
            button.textContent = originalText;
            button.classList.remove('success');
            button.disabled = false;
          }, 2000);
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('[Blushi] Quick add error:', error);
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      }
    },

    // Wishlist Toggle Handler
    handleWishlistToggle(button) {
      const productId = button.dataset.productId;
      const isActive = button.classList.toggle('active');
      
      // Get existing wishlist
      let wishlist = JSON.parse(localStorage.getItem('blushi_wishlist') || '[]');
      
      if (isActive) {
        if (!wishlist.includes(productId)) {
          wishlist.push(productId);
        }
        button.querySelector('svg').setAttribute('fill', 'currentColor');
      } else {
        wishlist = wishlist.filter(id => id !== productId);
        button.querySelector('svg').setAttribute('fill', 'none');
      }
      
      localStorage.setItem('blushi_wishlist', JSON.stringify(wishlist));
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist } }));
    },

    // Animate products on filter
    animateProducts() {
      const products = document.querySelectorAll('.boost-pfs-filter-product-item');
      products.forEach((product, index) => {
        product.style.opacity = '0';
        product.style.transform = 'translateY(20px)';
        
        setTimeout(() => {
          product.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
          product.style.opacity = '1';
          product.style.transform = 'translateY(0)';
        }, index * 50);
      });
    },

    // Utility: Format money
    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return this.config.moneyFormat.replace('{{amount}}', amount);
    },

    // Utility: Check if product is new (within 30 days)
    isNewProduct(product) {
      if (!product.published_at) return false;
      const publishedDate = new Date(product.published_at);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return publishedDate > thirtyDaysAgo;
    },

    // Utility: Get pagination range
    getPaginationRange(current, total) {
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      
      if (current <= 4) {
        return [1, 2, 3, 4, 5, '...', total];
      }
      
      if (current >= total - 3) {
        return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
      }
      
      return [1, '...', current - 1, current, current + 1, '...', total];
    },

    // Utility: Get color hex from name
    getColorHex(colorName) {
      const colors = {
        'black': '#000000',
        'white': '#FFFFFF',
        'red': '#DC2626',
        'blue': '#2563EB',
        'green': '#16A34A',
        'yellow': '#EAB308',
        'pink': '#F4C2C2',
        'blush': '#F4C2C2',
        'sage': '#A3B18A',
        'cream': '#F9F6F2',
        'rose': '#FB7185',
        'coral': '#F97316',
        'lavender': '#C4B5FD',
        'mint': '#86EFAC',
        'nude': '#E5D3C8',
        'berry': '#9F1239',
        'champagne': '#F7E7CE',
        'gold': '#CA8A04',
        'silver': '#9CA3AF',
        'bronze': '#CD7F32',
        'copper': '#B87333',
        'natural': '#D4C4B0',
        'beige': '#F5F5DC',
        'tan': '#D2B48C',
        'brown': '#8B4513',
        'navy': '#1E3A5F',
        'charcoal': '#363636',
        'grey': '#6B7280',
        'gray': '#6B7280',
      };
      return colors[colorName] || '#E5E7EB';
    },

    // Utility: Escape HTML
    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => BlushiBoost.init());
  } else {
    BlushiBoost.init();
  }

  // Expose for external access
  window.BlushiBoost = BlushiBoost;
})();
